import React, { useEffect, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { PUBLISHED_STATUS } from '../../../../model/FestivalData';
import { liveVersionChange } from '../../../../utils/publishing';
import Modal from '../../../../components/Modal';
import { useMessages } from '../../../../i18n/I18n';
import './PublishDialog.css';

interface PublishDialogProps {
  version: number;
  /** The status the version is about to get */
  status: PUBLISHED_STATUS;
  unsavedChanges: number;
  isSaving: boolean;
  /** Every version of the festival with its saved status, to tell which one the apps will show */
  loadVersions: () => Promise<{ version: number; status: PUBLISHED_STATUS }[]>;
  onConfirm: () => void;
  onCancel: () => void;
}

type LiveChange = { before: number | null; after: number | null };

/** Asks before a version is published or taken back to draft, saying which version the apps will show */
const PublishDialog: React.FC<PublishDialogProps> = ({
  version, status, unsavedChanges, isSaving, loadVersions, onConfirm, onCancel,
}) => {
  const t = useMessages('editor');
  const common = useMessages('common');
  const [change, setChange] = useState<LiveChange | 'loading' | 'unknown'>('loading');

  useEffect(() => {
    let cancelled = false;
    loadVersions()
      .then(versions => !cancelled && setChange(liveVersionChange(versions, version, status)))
      .catch(() => !cancelled && setChange('unknown'));
    return () => {
      cancelled = true;
    };
  }, [loadVersions, version, status]);

  const publishing = status === 'published';
  const leavesAppsEmpty = change !== 'loading' && change !== 'unknown' && change.after === null;

  const describeChange = (): string => {
    if (change === 'loading') return t.checkingLiveVersion;
    if (change === 'unknown') return t.liveVersionUnknown;
    const { before, after } = change;
    if (publishing) {
      if (after !== version) {
        return t.newerStaysLive(after, version);
      }
      return before === null || before === version
        ? t.appsStartShowing(version)
        : t.appsShowInPlaceOf(version, before);
    }
    if (before !== version) return t.appsKeepShowing(before);
    return after === null
      ? t.appsLeftEmpty
      : t.appsGoBackTo(after);
  };

  return (
    <Modal isOpen onClose={onCancel} className="publish-dialog">
      <h3>{publishing ? t.publishQuestion(version) : t.unpublishQuestion(version)}</h3>
      <p className={`publish-dialog-impact${leavesAppsEmpty ? ' warning' : ''}`}>
        {leavesAppsEmpty && <FiAlertTriangle aria-hidden="true" />}
        {describeChange()}
      </p>
      {unsavedChanges > 0 && (
        <p className="publish-dialog-unsaved">
          {t.unsavedSavedWithIt(unsavedChanges)}
        </p>
      )}
      <div className="modal-actions">
        <button type="button" className={leavesAppsEmpty ? 'danger' : undefined} onClick={onConfirm} disabled={isSaving}>
          {isSaving ? t.saving : publishing ? t.publishVersion(version) : t.moveToDraft}
        </button>
        <button type="button" onClick={onCancel}>{common.cancel}</button>
      </div>
    </Modal>
  );
};

export default PublishDialog;
